/**
 * Centralized API error handling helpers.
 * Use in API client and entityClient to avoid duplicated auth/status logic.
 *
 * @see docs/codebase_optimization_plan.md Section 4.3 & 8.2
 */

/** Error shape from API client (may have status, message, response, etc.) */
export interface ApiErrorLike {
  message?: string;
  status?: number;
  statusCode?: number;
  response?: { status?: number; data?: { message?: string } };
}

/**
 * Normalize unknown error to a string message and optional status code.
 */
function getErrorDetails(error: unknown): { message: string; status?: number } {
  if (error == null) return { message: 'An unexpected error occurred' };
  const err = error as ApiErrorLike;
  const message =
    err.message ??
    err.response?.data?.message ??
    (typeof error === 'string' ? error : 'An unexpected error occurred');
  const status =
    err.status ??
    err.statusCode ??
    err.response?.status ??
    (String(message).match(/\b(401|403|404|500)\b/)?.[1]
      ? parseInt(String(message).match(/\b(401|403|404|500)\b/)![1], 10)
      : undefined);
  return { message: String(message), status };
}

/**
 * True if the error indicates authentication failure (401 / Unauthorized).
 * Use to force re-login or clear token instead of falling back to cache.
 */
export function isAuthError(error: unknown): boolean {
  const { message, status } = getErrorDetails(error);
  const code = status ?? (message.match(/\b(\d{3})\b/)?.[1]
    ? parseInt(message.match(/\b(\d{3})\b/)![1], 10)
    : undefined);
  return (
    code === 401 ||
    /401|Unauthorized|Authentication required|Invalid or expired token/i.test(message)
  );
}

/**
 * Extract HTTP status code from error if present.
 */
export function getStatusCode(error: unknown): number | undefined {
  return getErrorDetails(error).status;
}

/**
 * User-facing message for common API errors.
 * Use in toasts, alerts, or form error state.
 */
export function getUserMessage(error: unknown): string {
  const { message, status } = getErrorDetails(error);
  if (status === 401 || isAuthError(error)) {
    return 'Please log in again.';
  }
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return 'Not found.';
  if (status === 422) return message || 'Invalid input.';
  if (status && status >= 500) return 'Something went wrong. Please try again.';
  return message || 'Something went wrong.';
}
